import {
  Alert,
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssets } from "../../assets/api/assets";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";
import {
  usePayout,
  usePayouts,
  useProducerAssetBalances,
} from "../api/payouts";

const AddPayoutModal = ({ open, onClose, onPayoutCreated, editingPayout }) => {
  const { t } = useTranslation("payouts");
  const [formData, setFormData] = useState({
    paid_to: "",
    amount: "",
    asset: "",
    notes: "",
  });
  const [lineItems, setLineItems] = useState([]);

  const { createPayout, creating } = usePayouts();
  const { project } = useProject();
  const { assets, loading: assetsLoading, error: assetsError } = useAssets();
  const { updatePayout, updating } = usePayout(editingPayout?.id);
  const { balances, loading: balancesLoading } = useProducerAssetBalances(
    formData.paid_to || null,
  );

  const [error, setError] = useState(null);
  const loading = creating || updating;

  const members =
    project?.users?.map((pu) => ({
      id: pu.user_details?.id,
      name: pu.user_details?.name,
      email: pu.user_details?.email,
      role: pu.role,
    })) || [];

  const dataLoading = assetsLoading;
  const dataError = assetsError;

  // Populate line items when balances load (only for new payouts)
  useEffect(() => {
    if (!editingPayout && balances.length > 0) {
      setLineItems(
        balances.map((b) => ({
          asset_id: b.asset_id,
          asset_title: b.asset_title,
          owed: b.remaining,
          amount: "",
        })),
      );
    } else if (balances.length === 0) {
      setLineItems([]);
    }
  }, [balances, editingPayout]);

  useEffect(() => {
    if (open) {
      if (editingPayout) {
        setFormData({
          paid_to: editingPayout.paid_to,
          amount: editingPayout.amount,
          asset: editingPayout.asset || "",
          notes: editingPayout.notes || "",
        });
        setLineItems([]);
      } else {
        setFormData({
          paid_to: "",
          amount: "",
          asset: "",
          notes: "",
        });
        setLineItems([]);
      }
      setError(null);
    }
  }, [open, editingPayout]);

  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
  }, [dataError]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (idx, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], amount: value };
      return updated;
    });
  };

  const handleProRata = () => {
    const total = parseFloat(formData.amount) || 0;
    if (total <= 0) return;
    const totalOwed = lineItems.reduce(
      (sum, li) => sum + Math.max(parseFloat(li.owed) || 0, 0),
      0,
    );
    if (totalOwed <= 0) return;
    setLineItems((prev) =>
      prev.map((li) => ({
        ...li,
        amount: (
          (Math.max(parseFloat(li.owed) || 0, 0) / totalOwed) *
          total
        ).toFixed(2),
      })),
    );
  };

  const validateForm = () => {
    if (!formData.paid_to) {
      setError(t("validation.recipientRequired"));
      return false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError(t("validation.amountGreaterThanZero"));
      return false;
    }
    const filledItems = lineItems.filter(
      (li) => li.amount !== "" && parseFloat(li.amount) > 0,
    );
    if (filledItems.length > 0) {
      const lineTotal = filledItems.reduce(
        (sum, li) => sum + parseFloat(li.amount),
        0,
      );
      if (Math.abs(lineTotal - parseFloat(formData.amount)) > 0.01) {
        setError(t("validation.lineItemsMustEqualTotal"));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setError(null);

      const filledItems = lineItems.filter(
        (li) => li.amount !== "" && parseFloat(li.amount) > 0,
      );

      const payoutData = {
        paid_to: formData.paid_to,
        amount: parseFloat(formData.amount),
        notes: formData.notes.trim() || undefined,
      };

      if (filledItems.length > 0) {
        payoutData.line_items = filledItems.map((li) => ({
          asset: li.asset_id,
          amount: parseFloat(li.amount),
        }));
      } else if (formData.asset) {
        payoutData.asset = formData.asset;
      }

      if (editingPayout) {
        await updatePayout(payoutData);
      } else {
        await createPayout(payoutData);
      }

      onPayoutCreated();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "An error occurred while creating the payout";
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({ paid_to: "", amount: "", asset: "", notes: "" });
    setLineItems([]);
    setError(null);
    onClose();
  };

  const showAttributionTable =
    !editingPayout && lineItems.length > 0 && !balancesLoading;

  const totalOwed = lineItems.reduce(
    (sum, li) => sum + Math.max(parseFloat(li.owed) || 0, 0),
    0,
  );
  const proRataDisabled =
    !formData.amount || parseFloat(formData.amount) <= 0 || totalOwed <= 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingPayout ? t("modal.titleEdit") : t("modal.titleAdd")}
      </DialogTitle>
      <DialogContent>
        {dataLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: SPACING.sm }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: SPACING.sm }}>
              <InputLabel>{t("modal.paidTo")}</InputLabel>
              <Select
                value={formData.paid_to}
                label={t("modal.paidTo")}
                onChange={(e) => handleInputChange("paid_to", e.target.value)}
              >
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t("modal.amount")}
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange("amount", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ mb: SPACING.sm }}
            />

            {showAttributionTable && (
              <Box sx={{ mb: SPACING.sm }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="bodySm" fontWeight="medium">
                    {t("modal.attributionLabel")}
                  </Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    onClick={handleProRata}
                    disabled={proRataDisabled}
                  >
                    {t("modal.proRata")}
                  </MuiButton>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("modal.assetColumn")}</TableCell>
                      <TableCell align="right">
                        {t("modal.owedColumn")}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        {t("modal.attributeColumn")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((li, idx) => (
                      <TableRow key={li.asset_id}>
                        <TableCell>
                          <Typography variant="bodySm">
                            {li.asset_title}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="bodySm">
                            ${parseFloat(li.owed).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={li.amount}
                            onChange={(e) =>
                              handleLineItemChange(idx, e.target.value)
                            }
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {!showAttributionTable && !editingPayout && (
              <FormControl fullWidth sx={{ mb: SPACING.sm }}>
                <InputLabel>{t("modal.asset")}</InputLabel>
                <Select
                  value={formData.asset}
                  label={t("modal.asset")}
                  onChange={(e) => handleInputChange("asset", e.target.value)}
                >
                  {assets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {editingPayout && (
              <FormControl fullWidth sx={{ mb: SPACING.sm }}>
                <InputLabel>{t("modal.asset")}</InputLabel>
                <Select
                  value={formData.asset}
                  label={t("modal.asset")}
                  onChange={(e) => handleInputChange("asset", e.target.value)}
                >
                  {assets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label={t("modal.notes")}
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder={t("modal.notesPlaceholder")}
              multiline
              rows={3}
              sx={{ mb: SPACING.sm }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="secondary" disabled={loading}>
          {t("modal.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || dataLoading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : editingPayout ? (
            t("modal.update")
          ) : (
            t("modal.create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPayoutModal;
